<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useDealsStore } from 'dashboard/stores/deals';

import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import DealBoardColumn from 'dashboard/components-next/Deals/DealBoardColumn.vue';
import DealCreateDialog from 'dashboard/components-next/Deals/DealCreateDialog.vue';
import DealDetailDialog from 'dashboard/components-next/Deals/DealDetailDialog.vue';
import StageManagerDialog from 'dashboard/components-next/Deals/StageManagerDialog.vue';

const { t } = useI18n();
const dealsStore = useDealsStore();

const createDialogRef = ref(null);
const detailDialogRef = ref(null);
const stageManagerRef = ref(null);
const newPipelineName = ref('');

const pipelines = computed(() => dealsStore.pipelines);
const activePipeline = computed(() => dealsStore.activePipeline);
const stages = computed(() => dealsStore.stages);
const dealsByStageId = computed(() => dealsStore.dealsByStageId);
const uiFlags = computed(() => dealsStore.uiFlags);

const isLoading = computed(
  () => uiFlags.value.fetchingPipelines || uiFlags.value.fetchingDeals
);

const loadDeals = () => {
  if (!activePipeline.value) return;
  dealsStore.fetchDeals({ pipelineId: activePipeline.value.id });
};

// `immediate` copre il rientro nella pagina con le pipeline gia' in cache; al primo
// mount scatta a vuoto e ricarica quando fetchPipelines valorizza la pipeline attiva.
watch(() => dealsStore.activePipelineId, loadDeals, { immediate: true });

onMounted(() => {
  if (!dealsStore.pipelines.length) dealsStore.fetchPipelines();
});

const onCreatePipeline = async () => {
  const name = newPipelineName.value.trim();
  if (!name || uiFlags.value.creatingPipeline) return;
  try {
    await dealsStore.createPipeline({ name });
    newPipelineName.value = '';
    useAlert(t('DEALS.CREATE_PIPELINE.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(error.message || t('DEALS.CREATE_PIPELINE.ERROR_MESSAGE'));
  }
};

const onMove = payload => dealsStore.moveDeal(payload);

const onAddDeal = stage => createDialogRef.value?.open(stage);

const onOpenDeal = deal => detailDialogRef.value?.open(deal);

const onManageStages = () => stageManagerRef.value?.open();

// Refresh silenzioso dopo modifiche dalla scheda (aggiorna il conteggio conversazioni
// senza far lampeggiare lo spinner della board).
const onDealUpdated = () => {
  if (activePipeline.value) {
    dealsStore.fetchDeals(
      { pipelineId: activePipeline.value.id },
      { silent: true }
    );
  }
};

const onCreateDeal = async payload => {
  try {
    await dealsStore.createDeal({
      ...payload,
      pipelineId: activePipeline.value.id,
    });
    createDialogRef.value?.onSuccess();
    useAlert(t('DEALS.CREATE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(error.message || t('DEALS.CREATE.API.ERROR_MESSAGE'));
  }
};
</script>

<template>
  <section
    class="flex flex-col w-full h-full gap-4 p-6 overflow-hidden bg-n-background"
  >
    <header class="flex items-center justify-between gap-3">
      <h1 class="text-xl font-medium text-n-slate-12">
        {{ t('DEALS.HEADER') }}
      </h1>
      <div class="flex items-center gap-2">
        <div v-if="pipelines.length > 1" class="flex items-center gap-1">
          <Button
            v-for="pipeline in pipelines"
            :key="pipeline.id"
            :label="pipeline.name"
            size="sm"
            :variant="pipeline.id === activePipeline?.id ? 'solid' : 'ghost'"
            @click="dealsStore.setActivePipeline(pipeline.id)"
          />
        </div>
        <Button
          v-if="activePipeline"
          size="sm"
          variant="faded"
          color="slate"
          icon="i-lucide-settings-2"
          :label="t('DEALS.STAGES.MANAGE')"
          @click="onManageStages"
        />
      </div>
    </header>

    <div v-if="isLoading" class="flex items-center justify-center grow">
      <Spinner />
    </div>

    <div
      v-else-if="!activePipeline"
      class="flex flex-col items-center justify-center gap-4 grow"
    >
      <p class="text-sm text-n-slate-11">{{ t('DEALS.EMPTY_PIPELINE') }}</p>
      <div class="flex items-center gap-2">
        <input
          v-model="newPipelineName"
          type="text"
          :placeholder="t('DEALS.CREATE_PIPELINE.PLACEHOLDER')"
          class="h-8 px-3 text-sm rounded-lg outline outline-1 outline-n-weak bg-n-alpha-2 text-n-slate-12 focus:outline-n-brand"
          @keydown.enter="onCreatePipeline"
        />
        <Button
          :label="t('DEALS.CREATE_PIPELINE.ACTION')"
          size="sm"
          :is-loading="uiFlags.creatingPipeline"
          :disabled="!newPipelineName.trim() || uiFlags.creatingPipeline"
          @click="onCreatePipeline"
        />
      </div>
    </div>

    <div v-else class="flex gap-4 overflow-x-auto grow items-stretch">
      <DealBoardColumn
        v-for="stage in stages"
        :key="stage.id"
        :stage="stage"
        :deals="dealsByStageId[stage.id] || []"
        @move="onMove"
        @add-deal="onAddDeal"
        @open="onOpenDeal"
      />
    </div>

    <DealCreateDialog
      ref="createDialogRef"
      :is-loading="uiFlags.creatingDeal"
      @create="onCreateDeal"
    />

    <DealDetailDialog
      ref="detailDialogRef"
      @updated="onDealUpdated"
      @deleted="onDealUpdated"
    />

    <StageManagerDialog
      ref="stageManagerRef"
      :pipeline-id="activePipeline?.id || null"
    />
  </section>
</template>
